import React, { useState, useEffect, useRef } from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { createIOSSafariClickHandler } from '../../utils/iosSafariTouchFix';
import { useTheme } from '../../context/ThemeContext';

const InlineEditableField = ({
  label,
  value,
  onSave,
  type = "text",
  placeholder,
  validation,
  multiline = false,
  className = "",
  disabled = false,
  required = false,
  inputProps = {}
}) => {
  const { isDarkMode } = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(value || '');
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);

  // Update local value when prop changes
  useEffect(() => {
    setCurrentValue(value || '');
  }, [value]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      // For text inputs, select all text
      if (type === 'text' || type === 'email' || type === 'url') {
        inputRef.current.select();
      }
    }
  }, [isEditing, type]);

  const handleClick = () => {
    if (!disabled) {
      setIsEditing(true);
      setError(null);
    }
  };

  // Create iOS Safari touch handler for inline editing
  const editHandlers = !disabled 
    ? createIOSSafariClickHandler(handleClick)
    : {};

  const handleBlur = async () => {
    if (currentValue === value) {
      // No changes made
      setIsEditing(false);
      return;
    }

    await handleSave();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleSave = async () => {
    const trimmedValue = currentValue.trim();
    
    // Validation
    if (required && !trimmedValue) {
      setError(`${label} is required`);
      return;
    }

    if (validation && !validation(trimmedValue)) {
      setError('Please enter a valid value');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave(trimmedValue);
      setIsEditing(false);
    } catch (err) {
      // Handle different types of errors
      if (err.message.includes('network') || err.message.includes('fetch')) {
        setError('Network error. Please check your connection and try again.');
      } else if (err.message.includes('permission') || err.message.includes('unauthorized')) {
        setError('You do not have permission to make this change.');
      } else {
        setError(err.message || 'Failed to save changes. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setCurrentValue(value || '');
    setIsEditing(false);
    setError(null);
  };

  const getInputType = () => {
    // Use appropriate mobile keyboard types
    switch (type) {
      case 'email':
        return 'email';
      case 'tel':
        return 'tel';
      case 'url':
        return 'url';
      default:
        return 'text';
    }
  };

  const renderInput = () => {
    const baseInputClasses = `
      w-full px-4 py-3 text-base border-2 rounded-lg transition-colors duration-200 min-h-[44px] shadow-sm
      ${isDarkMode 
        ? 'bg-gray-700 border-purple-500 text-gray-100 placeholder-gray-400 focus:border-purple-500 focus:ring-purple-500' 
        : 'bg-white border-purple-400 text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:ring-purple-500'
      }
      focus:outline-none focus:ring-2 focus:ring-opacity-50
      ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
    `;

    if (multiline) {
      return (
        <textarea
          ref={inputRef}
          value={currentValue}
          onChange={(e) => setCurrentValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`${baseInputClasses} resize-none`}
          rows={3}
          disabled={isSaving}
          {...inputProps}
        />
      );
    }

    return (
      <input
        ref={inputRef}
        type={getInputType()}
        value={currentValue}
        onChange={(e) => setCurrentValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={baseInputClasses}
        disabled={isSaving}
        {...inputProps}
      />
    );
  };

  const displayValue = value || placeholder || 'Click to add...';
  const isEmpty = !value;

  return (
    <div className={`${className}`}>
      {label && (
        <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        {!isEditing ? (
          // Display mode
          <div
            {...editHandlers}
            className={`
              min-h-[44px] px-4 py-3 rounded-lg cursor-pointer transition-colors duration-200
              border ${isDarkMode ? 'border-gray-600 hover:border-purple-400' : 'border-gray-200 hover:border-purple-300'}
              ${disabled 
                ? 'cursor-not-allowed opacity-50' 
                : isDarkMode 
                  ? 'hover:bg-gray-800/50' 
                  : 'hover:bg-gray-50'
              }
              ${isEmpty 
                ? isDarkMode 
                  ? 'text-gray-400' 
                  : 'text-gray-500' 
                : isDarkMode 
                  ? 'text-gray-100' 
                  : 'text-gray-900'
              }
            `}
          >
            <span className="text-base leading-relaxed break-words overflow-wrap-anywhere word-break-break-all">
              {displayValue}
            </span>
          </div>
        ) : (
          // Edit mode
          <div>
            {renderInput()}
            
            {/* Action buttons for mobile */}
            <div className="flex items-center justify-end space-x-2 mt-2">
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className={`
                  p-2 rounded-md transition-colors duration-200
                  ${isDarkMode 
                    ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }
                  disabled:opacity-50 disabled:cursor-not-allowed
                `}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className={`
                  p-2 rounded-md transition-colors duration-200
                  ${isDarkMode 
                    ? 'text-green-400 hover:text-green-300 hover:bg-gray-700' 
                    : 'text-green-600 hover:text-green-700 hover:bg-gray-100'
                  }
                  disabled:opacity-50 disabled:cursor-not-allowed
                `}
              >
                {isSaving ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : (
                  <CheckIcon className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p className="mt-1 text-sm text-red-500">
          {error}
        </p>
      )}
    </div>
  );
};

export default InlineEditableField;